import {
	AEM_DICTIONARY_COLLECTIONS,
	useAEMDictionary
} from "@AEM/hooks/useAEMDictionary"
import { showErrorNotification } from "@components/ui/Notification/Notification"
import { useOTPMutations } from "@hooks/useOTPMutations"
import { sendEmailOTP } from "@pages/profile-managment/services/agentService"
import { MFA_METHODS } from "@utils/constants.util"
import { isAxiosError } from "axios"
import { useSecurityMutations } from "../hooks/useSecurityMutations"
import { UpdateSecurityRequest } from "../services/securityService"
import { useSecuritySectionStore } from "../stores/securitySectionStore"
import { GenericVerificationFlow } from "./GenericVerificationFlow"

export const EmailChange = () => {
	const { user, updateUser, goToIdle, setToken, token, currentStep } =
		useSecuritySectionStore()
	const { validateOTP, isLoading } = useOTPMutations()
	const { updateSecurity } = useSecurityMutations()
	const { dictionary } = useAEMDictionary(
		AEM_DICTIONARY_COLLECTIONS.REGISTRATION
	)

	const validateEmail = (value: string) => {
		const {
			emailRequiredMessage = "Email is required",
			invalidEmailMessage = "Invalid email format",
			newEmailMustBeDifferentMessage = "New email must be different"
		} = dictionary || {}
		if (currentStep?.data?.validate) return null
		if (!value) return emailRequiredMessage
		if (!/^\S+@\S+\.\S+$/.test(value)) return invalidEmailMessage
		if (value === user?.email) return newEmailMustBeDifferentMessage
		return null
	}

	const handleSend = async (email: string) => {
		const errorMessage = dictionary?.emailAlreadyRegistered
		const result = await sendEmailOTP(email, errorMessage)
		setToken(result.token)
		return result.success
	}

	const handleVerify = async (otp: string, input: string) => {
		try {
			const otpValidation = await validateOTP.mutateAsync({
				token: token ?? "",
				otp
			})
			if (otpValidation?.data?.success) {
				await handleSuccess(input ?? "", otpValidation?.data?.token ?? "")
				return true
			}
		} catch {}
		return false
	}

	const handleSuccess = async (email: string, token: string) => {
		const shouldSetPrimary = !user?.primaryMFA
		const payload: UpdateSecurityRequest = {
			token: token ?? "",
			setEmailValidated: true,
			...(shouldSetPrimary ? { primaryMFA: MFA_METHODS.EMAIL } : {})
		}
		if (!currentStep?.data?.validate) {
			payload.changedEmail = email
		}

		try {
			await updateSecurity.mutateAsync(payload)
			updateUser({
				email,
				emailVerified: true,
				...(shouldSetPrimary ? { primaryMFA: MFA_METHODS.EMAIL } : {})
			})
			goToIdle()
		} catch (error) {
			const isEmailAlreadyExists =
				isAxiosError(error) &&
				error.response?.data?.message?.errorCode === "EMAIL_ALREADY_EXISTS"
			showErrorNotification(
				isEmailAlreadyExists
					? dictionary?.emailAlreadyRegistered ||
							"Failed to update security settings"
					: "Failed to update security settings"
			)
		}
	}

	return (
		<GenericVerificationFlow
			title={
				user?.emailVerified
					? dictionary?.updateYourEmailAddressLabel || ""
					: dictionary?.verifyYourEmailText || ""
			}
			description={
				user?.emailVerified
					? dictionary?.updateYourEmailAddressDescription || ""
					: dictionary?.verifyYourEmailDescription || ""
			}
			inputLabel={dictionary?.enterYourNewEmailAddressLabel || ""}
			inputPlaceholder="amy@agency.xyz"
			inputType="email"
			validateInput={validateEmail}
			onSend={handleSend}
			onVerify={handleVerify}
			resendFunction={handleSend}
			isLoading={isLoading}
			goBack={goToIdle}
			initialValue={currentStep?.data?.validate ? user?.email : ""}
			inputDisabled={Boolean(currentStep?.data?.validate)}
		/>
	)
}
